/** The Zod bound on `message` at `ask-request.schema.ts`. Kept here so the
 * composer and the backend limit cannot drift apart. */
export const ASK_MESSAGE_MAX_LENGTH = 4000;

/** The Zod bound on a context item's `value` at `ask-request.schema.ts`. Kept
 * here for the same reason as above — the client truncates to this, so the two
 * must not drift apart or a legitimately-truncated item starts 422ing. */
export const ASK_CONTEXT_VALUE_MAX_LENGTH = 20_000;

/**
 * Where in the app scope a question was asked from, authorization scope and
 * observability.
 */
export interface AskAppScope {
  groupId: string;
  companyId: string;
  route: string;
  screenTitle?: string;
}

/**
 * One fact about the app state the question was asked from, e.g.
 * `{ kind: 'grid_pagination', value: 'showing 1-10 of 173' }`.
 */
export interface AskContextItem {
  kind: string;
  value: string;
}

/**
 * A binary payload riding along with the question, base64-encoded inline.
 * `format` is a general MIME string rather than image-only, because non-image uploads are expected.
 */
export interface AskAttachment {
  origin: 'system' | 'user_upload';
  format: string;
  encoded: string;
  widthPx?: number;
  heightPx?: number;
  capturedAtMs?: number;
  filename?: string;
}

/** One visual's schema — never its values. `handle` is a short opaque id
 * (`v1`…`vN`), never the raw PowerBI GUID, mirroring the ordinal-label
 * precedent in `report-comments.ts`. */
export interface AskReportVisualManifest {
  handle: string;
  title: string;
  type: string;
  columns: string[];
  rowCount?: number;
}

/** The schema of every visual on the active page — never their values. */
export interface AskReportManifest {
  capturedAtMs: number;
  pageDisplayName: string;
  visuals: AskReportVisualManifest[];
  failedVisualTitles?: string[];
}

/** One active slicer's selection. */
export interface AskReportFilterValue {
  target: string;
  operator: string;
  values: string[];
}

/** The report's active filter state — a sibling of {@link AskReportManifest},
 * not nested inside it, so it cannot be silently omitted along with the rest
 * of a capture. */
export interface AskReportFilters {
  pageName: string;
  slicerFilters: AskReportFilterValue[];
}

/** One visual's fetched rows, in response to a prior tool call. */
export interface AskReportDataVisual {
  handle: string;
  columns: string[];
  rows: string[][];
  rowsTotal: number;
  tooLarge?: boolean;
}

export interface AskReportData {
  visuals: AskReportDataVisual[];
  unavailable: string[];
  packUnavailable: boolean;
  capturedAtMs: number;
}

/** Everything report-derived for one grounded question. Optional at the top
 * level of {@link AskRequest} — absence is the ungrounded signal. */
export interface AskReport {
  reportManifest: AskReportManifest;
  reportFilters: AskReportFilters;
  reportData?: AskReportData;
}

/** Body for `POST /ask`. */
export interface AskRequest {
  app: AskAppScope;
  report?: AskReport;
  sessionId?: string;
  message: string;
  context: AskContextItem[];
  attachments: AskAttachment[];
}

export interface AskToolCall {
  id?: string;
  name?: string;
  args?: Record<string, unknown>;
}

export interface AskResponse {
  reply: string;
  sessionId: string;
  toolCall?: AskToolCall;
}

export interface ProblemDetail {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [extension: string]: unknown;
}

export interface AskTurn {
  id: string;
  author: 'user' | 'agent' | 'tool';
  text: string;
}
